import { useState, Fragment } from "react";

// eslint-disable-next-line no-restricted-imports
import { css } from "@emotion/react";
import { useLocalizedHref } from "@libraries/web-framework";
import { useIntl, FormattedMessage } from "react-intl";

import { media, UNSAFE_media } from "@atlaskit/primitives/responsive";
import { token } from "@atlaskit/tokens";

import Button from "@/components/wpl/button/src";
import Image from "@/components/wpl/image/src";
import { DataEventProps } from "@/libraries/wpl/foundations/src";
import { Play } from "@/libraries/wpl/foundations/src/icons/object/play";
import { YoutubeModal } from "@/patterns/modal/src";

const confluenceHeroSectionStyle = css`
    display: flex;
    max-width: 1200px;
    flex-direction: column;
    margin: ${token("space.0")} auto ${token("space.0")} auto;

    ${media.above.sm} {
        flex-direction: row;
    }
`;

const heroSectionTextStyle = css`
    flex: 1;
    display: flex;
    flex-direction: column;
    align-items: center;
    text-align: center;
    margin-top: 50px;
    padding: ${token("space.400")} ${token("space.200")} ${token("space.0")}
        ${token("space.200")};

    h1 {
        color: #253858;
        font-size: ${token("space.400")};
        letter-spacing: ${token("space.0")};
        line-height: 1.25;
        font-weight: 500;
        margin: ${token("space.0")} ${token("space.0")} 14px ${token("space.0")};
    }

    p {
        color: #091e42;
        margin: ${token("space.0")} ${token("space.0")} ${token("space.300")}
            ${token("space.0")};
        font-size: 18px;
    }

    .confluence-hero-section-cta {
        margin: ${token("space.0")} ${token("space.0")} ${token("space.300")}
            ${token("space.0")};
        font-weight: 500;
    }

    ${UNSAFE_media.below.sm} {
        padding-top: 0;
    }

    ${media.above.sm} {
        flex: 0 0 41.66666%;
        text-align: left;
        align-items: flex-start;

        h1 {
            font-size: 44px;
            line-height: 1.18181818;
        }
    }

    ${media.above.lg} {
        flex: 0 0 41.66666%;

        h1 {
            font-size: ${token("space.600")};
            line-height: 3.5rem;
            margin-bottom: ${token("space.300")};
        }
    }
`;

const heroSectionImageStyle = css`
    ${UNSAFE_media.below.sm} {
        display: none;
    }

    ${media.above.sm} {
        flex: 0 0 50%;
        padding: ${token("space.400")} ${token("space.200")}
            ${token("space.200")} ${token("space.200")};
        margin-left: 30px;

        img {
            padding-top: ${token("space.250")};
            width: 100%;
        }
    }

    ${media.above.md} {
        position: relative;
    }

    ${media.above.lg} {
        margin-left: 30px;
        margin-right: 0;

        img {
            max-width: 100%;
        }
    }
`;

const videoCardWrapperStyles = css`
    padding: ${token("space.200")};
    margin-left: auto;
    margin-right: auto;
    display: flex;
    justify-content: center;

    @media (min-width: 540px) {
        width: 66.66666%;
    }

    ${media.above.sm} {
        width: 83.33333%;
        padding: ${token("space.400")} ${token("space.200")}
            ${token("space.200")} ${token("space.200")};
    }

    ${media.above.md} {
        width: 66.66666%;
    }
`;

const videoCardContainerStyles = css`
    display: flex;
    flex-direction: column;
    box-shadow: 1px 1px 6px rgba(0, 0, 0, 0.1);
    background-color: #ffffff;
    border-radius: 3px;
    max-width: 767.99px;

    ${media.above.sm} {
        flex-direction: row;
    }
`;

const videoCardImageStyles = css`
    position: relative;
    padding: 0 ${token("space.200")};
    overflow: hidden;

    ${media.above.sm} {
        flex: 5;
    }

    ${media.above.md} {
        height: 184px;
        padding-left: 28px;
        padding-right: 28px;
    }

    :hover::before {
        background-color: #ffffff;
        transform: scale(1.06);
    }

    ::before {
        position: absolute;
        content: "";
        top: 0;
        left: 0;
        width: 100%;
        height: 100%;
        margin: 0;
        background-position: center center;
        background-size: cover;
        background-color: rgba(23, 43, 77, 0.15);
        background-blend-mode: multiply;
        transform-origin: center center;
        transform: scale(1);
        transition: all 1.3s;
        background-image: url(https://wac-cdn-2.atlassian.com/image/upload/f_auto,q_auto/dam/jcr:83995a77-0155-4484-b349-39c9501f3313/Confluence%20PT%20thumbnail@2x.png?cdnVersion=1263);
        z-index: 1;
        border-top-left-radius: 3px;
        border-top-right-radius: 3px;

        ${media.above.sm} {
            border-top-left-radius: 3px;
            border-bottom-left-radius: 3px;
            border-top-right-radius: 0;
        }
    }

    img {
        max-height: 340px;
        opacity: 0;
        width: 100%;
    }

    svg {
        z-index: 2;
        position: absolute;
        top: 0;
        right: 0;
        left: 0;
        bottom: 0;
        margin: auto;
        fill: rgba(255, 255, 255, 0.9);
    }
`;

const videoCardTextStyles = css`
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: stretch;
    padding: ${token("space.400")};

    h3 {
        color: #253858;
        margin-top: 0;
        font-size: ${token("space.250")};
        letter-spacing: 0.3px;
        line-height: 1.2;
        margin-bottom: 14px;
        font-weight: 500;
    }

    ${media.above.sm} {
        flex: 7;

        h3 {
            font-size: ${token("space.300")};
        }
    }

    ${media.above.md} {
        padding: ${token("space.500")};

        h3 {
            line-height: ${token("space.400")};
        }
    }

    a {
        display: flex;
        align-items: center;
        color: #ffffff;

        span {
            font-size: ${token("space.200")};
            color: #0052cc;
        }

        svg {
            fill: #0052cc;
            margin-right: ${token("space.100")};
        }
    }
`;

const HeroSection = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const intl = useIntl();
    const getItFreeText = intl.formatMessage({
        id: "ConfluenceSoftwarePageTemplate.heroCTA",
        defaultMessage: "Get it free"
    });

    const videoCardDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "videoEmbedInline",
        "data-event": "clicked",
        "data-label": "watch the video_image"
    };

    const videoCardTextDataEventProps: DataEventProps = {
        "data-product-key": "confluence",
        "data-event-component": "linkButton",
        "data-event-container": "videoLink",
        "data-event": "clicked",
        "data-label-english": "Watch the video",
        "data-label": "Watch the video"
    };

    return (
        <Fragment>
            <section>
                <div css={confluenceHeroSectionStyle}>
                    <div css={heroSectionTextStyle}>
                        <h1>
                            <FormattedMessage
                                id="ConfluenceSoftwarePageTemplate.heroHeader"
                                defaultMessage="Accomplish more together"
                            />
                        </h1>
                        <p>
                            <FormattedMessage
                                id="ConfluenceSoftwarePageTemplate.heroBody"
                                defaultMessage="Confluence is your remote-friendly team workspace where knowledge and collaboration meet."
                            />
                        </p>
                        <Button
                            text={getItFreeText}
                            className="confluence-hero-section-cta"
                            color="yellow"
                            url={useLocalizedHref("/software/confluence/free")}
                            dataEventProps={{
                                "data-product-key": "confluence",
                                "data-event-component": "linkButton",
                                "data-event-container": "heroSection",
                                "data-event": "clicked",
                                "data-label": getItFreeText
                            }}
                        />
                    </div>
                    <div css={heroSectionImageStyle}>
                        <Image
                            alt={intl.formatMessage({
                                id: "ConfluenceSoftwarePageTemplate.heroImageAltText",
                                defaultMessage:
                                    "Illustration demonstrating workflows in Confluence"
                            })}
                            src="https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_Hero.webp"
                        />
                    </div>
                </div>
                <div
                    css={css`
                        position: relative;
                    `}
                >
                    <div css={videoCardWrapperStyles}>
                        <div css={videoCardContainerStyles}>
                            <a
                                css={videoCardImageStyles}
                                href="https://www.youtube.com/watch?v=jqg8o-ataIQ"
                                data-schema-version="1"
                                {...videoCardDataEventProps}
                                onClick={(event) => {
                                    event.preventDefault();
                                    setIsModalOpen(true);
                                }}
                            >
                                <img
                                    src="https://wac-cdn.atlassian.com/misc-assets/webp-images/Conf_ProductTour_Thumbnail.webp"
                                    alt={intl.formatMessage({
                                        id: "ConfluenceSoftwarePageTemplate.Hero.VideoThumbnailAltText",
                                        defaultMessage: "video thumbnail"
                                    })}
                                />
                                <Play size="70" />
                            </a>
                            <div css={videoCardTextStyles}>
                                <h3>
                                    <FormattedMessage
                                        id="ConfluenceSoftwarePageTemplate.Hero.VideoHeader"
                                        defaultMessage={
                                            "See team collaboration come to life"
                                        }
                                    />
                                </h3>
                                <a
                                    css={css`
                                        :hover {
                                            svg,
                                            span {
                                                color: #0065ff;
                                                fill: #0065ff;
                                            }
                                        }
                                    `}
                                    href="https://www.youtube.com/watch?v=jqg8o-ataIQ"
                                    {...videoCardTextDataEventProps}
                                    data-schema-version="1"
                                    onClick={(event) => {
                                        event.preventDefault();
                                        setIsModalOpen(true);
                                    }}
                                >
                                    <Play size="22" />
                                    <span>
                                        <FormattedMessage
                                            id="ConfluenceSoftwarePageTemplate.Hero.PlayVideo"
                                            defaultMessage={"Watch the video"}
                                        />
                                    </span>
                                </a>
                            </div>
                        </div>
                    </div>
                    <div
                        css={css`
                            position: absolute;
                            width: 100%;
                            background-color: #deebff;
                            z-index: -1;
                            bottom: 0;
                            height: 75%;
                        `}
                    />
                </div>
            </section>
            <YoutubeModal
                isOpen={isModalOpen}
                src="https://www.youtube.com/embed/jqg8o-ataIQ?autoplay=1&rel=0&origin=https%3A%2F%2Fwww.atlassian.com&widget_referrer=https%3A%2F%2Fwww.atlassian.com&enablejsapi=1&widgetid=1"
                title={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.Hero.ModalYouTubeIframeTitle",
                    defaultMessage:
                        "Say hello to Confluence! | Confluence Overview | Atlassian"
                })}
                closeIconLabel={intl.formatMessage({
                    id: "ConfluenceSoftwarePageTemplate.Hero.CloseModalButtonMobileLabel",
                    defaultMessage: "Close modal"
                })}
                closeModal={() => setIsModalOpen(false)}
            />
        </Fragment>
    );
};

export default HeroSection;
